
import { useState } from 'react';
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { calculateDurationDays } from './utils/date/durationUtils';
import { FormValues, CampaignCreationResult } from './types/campaignCreation';
import { Location } from '@/components/campaign/types';
import { logCampaignCreation } from './utils/campaignLogger';
import { useCampaignCreationService } from './services/campaignCreationService';
import { useUserStorage } from './utils/storageUtils';

export const useCampaignCreator = (setCurrentCampaignId?: (id: string) => void) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();
  const { user } = useAuth();
  const { getItem } = useUserStorage();
  const {
    createParentCampaign,
    handleCampaignCreationResult,
    validateStartDate
  } = useCampaignCreationService(setCurrentCampaignId);

  const createCampaign = async (
    formValues: FormValues,
    selectedLocations: Location[],
    creativeUrls: string[],
    totalDevices: number,
    budgetCalculations: any,
    detailsUrl: string | null,
    locationsUrl: string | null
  ): Promise<CampaignCreationResult> => {
    setIsLoading(true);
    try {
      // Validate locations
      if (!selectedLocations || selectedLocations.length === 0) {
        setIsLoading(false);
        return { error: new Error("No locations selected") };
      }

      // Validate and ensure we have a budget
      if (!budgetCalculations || !budgetCalculations.totalBudget) {
        const storedBudget = getItem('budgetCalculations');
        if (storedBudget) {
          try {
            budgetCalculations = JSON.parse(storedBudget);
          } catch (e) {
            console.error('Error parsing stored budget:', e);
          }
        }
        
        if (!budgetCalculations || !budgetCalculations.totalBudget) {
          const basePrice = totalDevices * 10;
          const totalBudget = basePrice * (formValues.duration || 7);
          
          budgetCalculations = {
            basePrice,
            totalBudget
          };
          
          formValues.totalBudget = totalBudget;
        } else {
          formValues.totalBudget = budgetCalculations.totalBudget;
        }
      } else {
        formValues.totalBudget = budgetCalculations.totalBudget;
      }

      // Get campaign name from user-specific localStorage
      const campaignName = getItem('campaignName') || `Campaign ${new Date().toLocaleDateString()}`;
      
      // Validate that the start date is at least 48 hours in the future
      if (!validateStartDate(formValues.startDate)) {
        setIsLoading(false);
        return { error: new Error("Invalid start date") };
      }
      
      // Calculate days between start and end date
      const durationDays = calculateDurationDays(formValues.startDate, formValues.endDate);

      // Ensure email is from authenticated user
      if (user?.email) {
        formValues.email = user.email;
      }
      
      // Prepare company info for database
      const companyInfo = {
        companyName: formValues.companyName,
        brandName: formValues.brandName,
        personInCharge: formValues.personInCharge,
        jobTitle: formValues.jobTitle,
        email: formValues.email,
        mobile: formValues.mobile,
        campaignName: campaignName,
        totalBudget: formValues.totalBudget
      };
      
      // Create the parent campaign
      const { campaignId, error } = await createParentCampaign(
        formValues,
        selectedLocations,
        creativeUrls,
        totalDevices,
        budgetCalculations,
        detailsUrl,
        locationsUrl,
        durationDays,
        companyInfo
      );
      
      if (error || !campaignId) {
        setIsLoading(false);
        return { error: error || new Error("Failed to create campaign") };
      }

      // Handle completion and navigation
      const result = handleCampaignCreationResult(campaignId, [], 1);
      return result;
    } catch (error: any) {
      console.error('Unexpected error during campaign creation:', error);
      return { error };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    createCampaign,
  };
};
